import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LocationProvider } from './context/LocationContext';

// Layout components
import AppLayout from './components/AppLayout';
import StaffLayout from './components/StaffLayout';
import ManagerLayout from './components/ManagerLayout';
import SuperAdminLayout from './components/SuperAdminLayout';

// Lazy-loaded pages
const Login = lazy(() => import('./pages/Login'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Register = lazy(() => import('./pages/Register'));
const BookingFlow = lazy(() => import('./pages/BookingFlow'));
const CustomerDashboard = lazy(() => import('./pages/customer/Dashboard'));
const MyAppointments = lazy(() => import('./pages/customer/Appointments'));
const MyLoyalty = lazy(() => import('./pages/customer/Loyalty'));
const MyProfile = lazy(() => import('./pages/customer/Profile'));
const MyGiftCards = lazy(() => import('./pages/customer/GiftCards'));
const SubmitReview = lazy(() => import('./pages/customer/SubmitReview'));
const StaffDashboard = lazy(() => import('./pages/staff/Dashboard'));
const StaffSchedule = lazy(() => import('./pages/staff/Schedule'));
const StaffCommissions = lazy(() => import('./pages/staff/Commissions'));
const StaffClients = lazy(() => import('./pages/staff/Clients'));
const ManagerDashboard = lazy(() => import('./pages/manager/Dashboard'));
const ManagerBookings = lazy(() => import('./pages/manager/Bookings'));
const ManagerStaff = lazy(() => import('./pages/manager/Staff'));
const ManagerShiftPlanner = lazy(() => import('./pages/manager/ShiftPlanner'));
const ManagerReports = lazy(() => import('./pages/manager/Reports'));
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'));
const AdminLocations = lazy(() => import('./pages/admin/Locations'));
const AdminUsers = lazy(() => import('./pages/admin/Users'));
const AdminServices = lazy(() => import('./pages/admin/Services'));
const AdminPayments = lazy(() => import('./pages/admin/Payments'));
const AdminServiceEditor = lazy(() => import('./pages/admin/ServiceEditor'));
const AdminAnalytics = lazy(() => import('./pages/admin/Analytics'));
const AdminWaivers = lazy(() => import('./pages/admin/Waivers'));
const AdminEmailProfiles = lazy(() => import('./pages/admin/EmailProfiles'));
const AdminSettings = lazy(() => import('./pages/admin/Settings'));
const AdminCoupons = lazy(() => import('./pages/admin/Coupons'));
const AdminCampaigns = lazy(() => import('./pages/admin/Campaigns'));
const MyWaivers = lazy(() => import('./pages/customer/Waivers'));
const MyWallet = lazy(() => import('./pages/customer/Wallet'));
const ManagerCustomers = lazy(() => import('./pages/manager/Customers'));
const ManagerGiftCards = lazy(() => import('./pages/manager/GiftCards'));
const ManagerAttendance = lazy(() => import('./pages/manager/Attendance'));

function Loading() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
      <div style={{ width: 36, height: 36, border: '3px solid #EBF5F3', borderTopColor: '#5B9E8F', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function homePortal(roles) {
  if (roles.some((r) => ['super_admin', 'admin', 'owner'].includes(r))) return '/admin';
  if (roles.some((r) => ['manager', 'franchise_owner'].includes(r))) return '/manager';
  if (roles.includes('staff')) return '/staff';
  return '/';
}
function ProtectedRoute({ children, roles }) {
  const { user, loading, isAuthenticated } = useAuth();
  if (loading) return <Loading />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  const userRoles = (user.roles && user.roles.length) ? user.roles : [user.role];
  if (roles && !roles.some((r) => userRoles.includes(r))) {
    return <Navigate to={homePortal(userRoles)} replace />;
  }
  return children || <Outlet />;
}

function App() {
  return (
    <AuthProvider>
      <LocationProvider>
      <BrowserRouter>
        <Suspense fallback={<Loading />}>
          <Routes>
            {/* Public */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/book" element={<BookingFlow />} />

            {/* Customer Portal */}
            <Route path="/" element={<ProtectedRoute roles={['customer']}><AppLayout /></ProtectedRoute>}>
              <Route index element={<CustomerDashboard />} />
              <Route path="appointments" element={<MyAppointments />} />
              <Route path="loyalty" element={<MyLoyalty />} />
              <Route path="wallet" element={<MyWallet />} />
              <Route path="profile" element={<MyProfile />} />
              <Route path="gift-cards" element={<MyGiftCards />} />
              <Route path="waivers" element={<MyWaivers />} />
              <Route path="book" element={<BookingFlow />} />
              <Route path="review/:bookingId" element={<SubmitReview />} />
            </Route>

            {/* Staff Dashboard */}
            <Route path="/staff" element={<ProtectedRoute roles={['staff']}><StaffLayout /></ProtectedRoute>}>
              <Route index element={<StaffDashboard />} />
              <Route path="schedule" element={<StaffSchedule />} />
              <Route path="commissions" element={<StaffCommissions />} />
              <Route path="clients" element={<StaffClients />} />
            </Route>

            {/* Manager Dashboard */}
            <Route path="/manager" element={<ProtectedRoute roles={['manager','franchise_owner']}><ManagerLayout /></ProtectedRoute>}>
              <Route index element={<ManagerDashboard />} />
              <Route path="bookings" element={<ManagerBookings />} />
              <Route path="staff" element={<ManagerStaff />} />
              <Route path="shifts" element={<ManagerShiftPlanner />} />
              <Route path="reports" element={<ManagerReports />} />
              <Route path="customers" element={<ManagerCustomers />} />
              <Route path="gift-cards" element={<ManagerGiftCards />} />
              <Route path="attendance" element={<ManagerAttendance />} />
            </Route>

            {/* Super Admin Dashboard */}
            <Route path="/admin" element={<ProtectedRoute roles={['super_admin','admin','owner']}><SuperAdminLayout /></ProtectedRoute>}>
              <Route index element={<AdminDashboard />} />
              <Route path="locations" element={<AdminLocations />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="services" element={<AdminServices />} />
              <Route path="services/:id/edit" element={<AdminServiceEditor />} />
              <Route path="payments" element={<AdminPayments />} />
              <Route path="analytics" element={<AdminAnalytics />} />
              <Route path="waivers" element={<AdminWaivers />} />
              <Route path="email-profiles" element={<AdminEmailProfiles />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="coupons" element={<AdminCoupons />} />
              <Route path="campaigns" element={<AdminCampaigns />} />
            </Route>

            {/* Catch-all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
      </LocationProvider>
    </AuthProvider>
  );
}

export default App;
